import type { TranslationKey } from '@/i18n-key';
import type { ThematicMeetingStatus } from '@/types/thematic-meetings';
import type { PillsVariant } from '@amelio/ui-library';

/** The subset of a meeting this module needs: matches both `ThematicMeeting` and
 * `ThematicMeetingListItem`, so the same logic serves the plan page and the list. */
export interface ScheduledMeeting {
    status: ThematicMeetingStatus;
    scheduledAt: string | null;
    durationMinutes: number | null;
}

/** The length the server gives the calendar invitation of a meeting that has no duration of its
 * own (`ThematicMeetingCalendarEventAssembler.DefaultDurationMinutes`). */
const DEFAULT_MEETING_DURATION_MINUTES = 30;

const MS_PER_MINUTE = 60_000;

/** A meeting is overdue when it is still planned and its time slot has ended — a meeting under way
 * is not late. A purely front-end rule, since the backend stores the date but never flags lateness
 * itself. */
export const isMeetingOverdue = (meeting: ScheduledMeeting, now: Date = new Date()): boolean => {
    if (meeting.status !== 'Planned' || meeting.scheduledAt == null) {
        return false;
    }

    const durationMinutes = meeting.durationMinutes ?? DEFAULT_MEETING_DURATION_MINUTES;
    const endsAt = new Date(meeting.scheduledAt).getTime() + durationMinutes * MS_PER_MINUTE;

    return endsAt < now.getTime();
};

/** The one status a meeting shows, on its card as on its plan: completed, overdue or upcoming — never
 * two at once, so a completed meeting is never shown as overdue, even past its original date. */
export type MeetingDisplayStatus = 'Completed' | 'Overdue' | 'Planned';

export interface MeetingStatusPill {
    state: MeetingDisplayStatus;
    labelKey: TranslationKey;
    variant: PillsVariant;
}

const MEETING_STATUS_PILLS: Readonly<Record<MeetingDisplayStatus, MeetingStatusPill>> = {
    Completed: {
        state: 'Completed',
        labelKey: 'thematicMeetings.status.completed',
        variant: 'activeTinted',
    },
    // The one state that asks for action carries the red, on the library's error tokens; the date
    // beside it stays neutral so the lateness is said once.
    Overdue: {
        state: 'Overdue',
        labelKey: 'thematicMeetings.list.overdueTag',
        variant: 'inactive',
    },
    Planned: {
        state: 'Planned',
        labelKey: 'thematicMeetings.status.planned',
        variant: 'draftTinted',
    },
};

/** The pill of a meeting's status, by priority: completed beats overdue beats upcoming. */
export const getMeetingStatusPill = (meeting: ScheduledMeeting): MeetingStatusPill => {
    if (meeting.status === 'Completed') {
        return MEETING_STATUS_PILLS.Completed;
    }

    return isMeetingOverdue(meeting) ? MEETING_STATUS_PILLS.Overdue : MEETING_STATUS_PILLS.Planned;
};
